import { Box, Flex, Text, VStack } from "@chakra-ui/react";
import { Item } from "./Item";
import { Bullet } from "@/models";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Info } from "@phosphor-icons/react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Icon, Input, Tooltip } from "barte-design-system";

interface ItemsList {
  tooltip?: string;
  optional?: boolean;
  label?: string;
  name?: string;
  id?: string;
  list: Bullet[];
  updateList: (values: Bullet[]) => void;
  buttonText: string;
}

type FormValues = {
  description: string;
};

const addBulletSchema = yup.object().shape({
  description: yup.string().required("Preenchimento obrigatório!"),
});

const ItemsList = ({
  tooltip,
  optional,
  label,
  list,
  updateList,
  buttonText,
}: ItemsList) => {
  const [inputAdd, setInputAdd] = useState(false);
  const [editBulletValue, setEditBulletValue] = useState("");
  const [bulletSelectedId, setBulletSelectedId] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(addBulletSchema),
  });

  function handleDeleteItem(indexList: number) {
    updateList(list.filter((bullet, index) => index !== indexList));
  }

  const onSubmit: SubmitHandler<FormValues> = (values, event) => {
    event.preventDefault();

    if (list) {
      updateList([
        ...list,
        { description: values.description, id: String(list.length + 1) },
      ]);
    } else {
      updateList([{ description: values.description, id: "1" }]);
    }

    reset({ description: "" });
    setInputAdd(false);
  };

  function handleChangeBulletValue() {
    if (editBulletValue !== "") {
      updateList(
        list.map((item) =>
          item.id === bulletSelectedId
            ? { ...item, description: editBulletValue }
            : item
        )
      );
    }

    setEditBulletValue("");
    setBulletSelectedId(null);
  }

  return (
    <Box w="100%">
      <Flex mb="2" align="center" gap="1">
        <Text color="var(--content-primary)" fontSize="14px" fontWeight="500">
          {label}
        </Text>
        {optional && (
          <Text color="var(--content-tertiary)" fontSize="14px">
            (opcional)
          </Text>
        )}
        {tooltip && (
          <Tooltip placement="right" label={tooltip}>
            <span>
              <Icon icon={Info} size={14} color="var(--content-tertiary)" />
            </span>
          </Tooltip>
        )}
      </Flex>

      <Box
        w="100%"
        border="1px solid var(--stroke-primary)"
        borderRadius="var(--border-radius-sm)"
        p="2"
      >
        <VStack align="stretch" spacing="1">
          {list?.map((item, index) =>
            item?.id === bulletSelectedId ? (
              <Flex key={item.id} gap="2" align="center">
                <Box flex="1">
                  <Input
                    type="text"
                    maxLength={50}
                    placeholder="Digite aqui"
                    aria-label="Editar item"
                    defaultValue={item.description}
                    onChange={(e) => setEditBulletValue(e.target.value)}
                  />
                </Box>
                <Button size="sm" onClick={handleChangeBulletValue}>
                  Ok
                </Button>
              </Flex>
            ) : (
              <Item
                key={item.id ?? index}
                text={item.description}
                index={index}
                handleDelete={handleDeleteItem}
                onClick={() => setBulletSelectedId(item.id)}
              />
            )
          )}

          {inputAdd ? (
            <Flex gap="2" align="flex-start">
              <Box flex="1">
                <Input
                  type="text"
                  placeholder="Digite aqui"
                  aria-label="Novo item"
                  maxLength={120}
                  error={errors?.description?.message as string}
                  {...register("description")}
                />
              </Box>
              <Button size="sm" onClick={handleSubmit(onSubmit)}>
                {buttonText}
              </Button>
            </Flex>
          ) : (
            <Flex py="2" px="2">
              <Button variant="link" onClick={() => setInputAdd(true)}>
                Adicionar item
              </Button>
            </Flex>
          )}
        </VStack>
      </Box>
    </Box>
  );
};

export { ItemsList };
